import React, { useState, useEffect } from 'react';
import { cancelSubscription, previewCancellation } from '../../api';
import ToastContainer from './ToastContainer';
import { useToast } from './useToast';
import { format } from 'date-fns';

interface PlanData {
  Id: number;
  Type: string;
  IsActive: boolean;
  PlanStartDate: string;
  PlanEndDate: string | null;
  RefreshFrequency: string;
  MonthsOfData: number;
  IncludeMarketInsights: boolean;
  TotalIntegrations: number;
  SubscriptionStatus: string;
  PendingPlanType: string | null;
  PendingPlanEffectiveDate: string | null;
  DiscountCouponName: string | null;
  DiscountEndDate: string | null;
  DiscountStripeId: string | null;
}

interface ActivePlanDetailsProps {
  planData: PlanData | null;
  loading?: boolean;
  numberOfActiveIntegrations: number;
  enableCancellationFunctionality: boolean;
  onNavigateToAccountsBilling?: () => void;
  onPlanCanceled?: () => void;
  enableManagePlanNavigationButton: boolean;
}

const ActivePlanDetails: React.FC<ActivePlanDetailsProps> = ({
  planData, loading, numberOfActiveIntegrations, enableCancellationFunctionality,
  onNavigateToAccountsBilling, onPlanCanceled, enableManagePlanNavigationButton }) => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellationDate, setCancellationDate] = useState<string>('');
  const [loadingCancellationDate, setLoadingCancellationDate] = useState(false);
  const [isCancellingPlan, setIsCancellingPlan] = useState(false);
  const { toasts, showSuccess, showError, removeToast } = useToast();

  const isActiveButCancelling = Boolean(
    planData?.SubscriptionStatus === 'cancel_at_period_end' &&
    planData?.PlanEndDate &&
    planData.IsActive
  );

  if (loading) {
    return (
      <div style={{
        background: '#fff',
        borderRadius: 8,
        padding: 24,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        marginBottom: 24
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 16,
            height: 16,
            border: '2px solid #f3f3f3',
            borderTop: '2px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          Loading plan details...
        </div>
      </div>
    );
  }

  if (!planData) {
    return (
      <div style={{
        background: '#fff',
        borderRadius: 8,
        padding: 24,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        marginBottom: 24,
        border: '2px solid #2196f3',
        textAlign: 'center'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#1976d2' }}>Ready to Get Started?</h3>
        <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '1.1rem' }}>
          You haven't selected a plan yet. Choose a plan to start using Execdash integrations and unlock powerful insights for your organization. If you have just purchased a plan, it may take a few moments for the payment to be processed and your plan to activate. Please try refreshing the page.
        </p>
        {onNavigateToAccountsBilling && (
          <button
            onClick={onNavigateToAccountsBilling}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              boxShadow: '0 2px 4px rgba(33, 150, 243, 0.3)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#1976d2';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#2196f3';
            }}
          >
            View Plans & Pricing
          </button>
        )}
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'do MMMM, yyyy');
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (isActive: boolean, isActiveButCancelling: boolean) => {
    if (isActiveButCancelling) {
      return '#f44336';
    }
    return isActive ? '#4caf50' : '#f44336';
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'cancel_at_period_end':
        return 'Expiring Soon';
      case 'canceled':
        return 'Canceling Soon';
      default:
        return 'Inactive';
    }
  };

  const isPlanExpired = (endDate: string | null) => {
    try {
      if (endDate) {
        const planEndDate = new Date(endDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
        return planEndDate < today;
      }
      return false;//no end date so it is ongoing

    } catch {
      return false;
    }
  };

  const handleOpenCancelModal = async () => {
    setShowCancelModal(true);
    setLoadingCancellationDate(true);

    try {
      const preview = await previewCancellation();
      setCancellationDate(preview.currentPeriodEnd);
    } catch (error: any) {
      console.error('Error fetching cancellation date:', error);
      showError('Failed to load cancellation details');
      setCancellationDate('Unknown');
    } finally {
      setLoadingCancellationDate(false);
    }
  };

  // Check if plan is expired
  const isExpired = planData ? isPlanExpired(planData.PlanEndDate) : false;

  // If plan exists but is expired, show expired message
  if (planData && isExpired) {
    return (
      <div style={{
        background: '#fff',
        borderRadius: 8,
        padding: 24,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        marginBottom: 24,
        border: '2px solid #f44336',
        textAlign: 'center'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#d32f2f' }}>⚠️ Plan Expired</h3>
        <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '1.1rem' }}>
          Your <strong>{planData.Type}</strong> plan expired on {planData.PlanEndDate ? formatDate(planData.PlanEndDate) : 'Unknown date'}.
        </p>
        <p style={{ margin: '0 0 20px 0', color: '#666' }}>
          To continue using ExecDash integrations and features, please renew your plan or select a new one.
        </p>
        {onNavigateToAccountsBilling && (
          <button
            onClick={onNavigateToAccountsBilling}
            style={{
              padding: '12px 24px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              boxShadow: '0 2px 4px rgba(244, 67, 54, 0.3)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#d32f2f';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#f44336';
            }}
          >
            Renew Plan
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{
      background: '#fff',
      borderRadius: 8,
      padding: 24,
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      marginBottom: 24,
      border: `2px solid ${getStatusColor(planData.SubscriptionStatus === 'active', isActiveButCancelling)}`
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>
            {planData.PendingPlanType && planData.PendingPlanEffectiveDate ? 
            `Plan: ${planData.Type.toUpperCase()} (Moving to: ${planData.PendingPlanType.toUpperCase()} on ${format(planData.PendingPlanEffectiveDate, 'do MMMM, yyyy')})` :
            `Plan: ${planData.Type.toUpperCase()}`}
          </h3>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '4px 12px',
            backgroundColor: getStatusColor(planData.SubscriptionStatus === 'active', isActiveButCancelling),
            color: 'white',
            borderRadius: 16,
            fontSize: '0.875rem',
            fontWeight: 'bold'
          }}>
            <div style={{
              width: 8,
              height: 8,
              backgroundColor: 'white',
              borderRadius: '50%'
            }} />
            {getStatusText(planData.SubscriptionStatus)}
          </div>
          {planData.DiscountCouponName && planData.DiscountEndDate && (
            <div style={{
              marginTop: 8,
              padding: '8px 12px',
              backgroundColor: '#e8f5e8',
              border: '1px solid #4caf50',
              borderRadius: 6,
              fontSize: '0.875rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#2e7d32' }}>
                <span style={{ fontSize: '1rem' }}>🎉</span>
                <strong>Free Trial Active: {planData.DiscountCouponName}</strong>
              </div>
              <div style={{ color: '#666', marginTop: 4 }}>
                Trial expires: {formatDate(planData.DiscountEndDate)}
              </div>
            </div>
          )}
        </div>
        {enableCancellationFunctionality && planData.IsActive && (
          <button
            onClick={handleOpenCancelModal}
            style={{
              padding: '8px 16px',
              backgroundColor: 'lightgrey',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              fontSize: '0.875rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              boxShadow: '0 2px 4px rgba(253, 252, 251, 0.85)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'grey';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'lightgrey';
            }}
          >
            Cancel Plan
          </button>
        )}
        {enableManagePlanNavigationButton && planData.IsActive && (
          <button
            onClick={onNavigateToAccountsBilling}
            style={{
              padding: '8px 16px',
              background: 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              fontSize: '0.875rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              boxShadow: '0 2px 4px rgba(244, 67, 54, 0.3)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#d32f2f';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#f44336';
            }}
          >
            Manage Plan
          </button>
        )}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: 20
      }}>
        {/* Plan Duration */}
        <div>
          <h4 style={{ margin: '0 0 8px 0', color: '#666', fontSize: '0.875rem', textTransform: 'uppercase' }}>
            Plan Duration
          </h4>
          <div style={{ color: '#333' }}>
            <div><strong>Start:</strong> {formatDate(planData.PlanStartDate)}</div>
            <div><strong>End:</strong> {planData.PlanEndDate ? formatDate(planData.PlanEndDate) : 'Ongoing'}</div>
          </div>
        </div>

        {/* Data Settings */}
        <div>
          <h4 style={{ margin: '0 0 8px 0', color: '#666', fontSize: '0.875rem', textTransform: 'uppercase' }}>
            Data Settings
          </h4>
          <div style={{ color: '#333' }}>
            <div><strong>Refresh:</strong> {planData.RefreshFrequency}</div>
            <div><strong>Data History:</strong> {planData.MonthsOfData} months</div>
          </div>
        </div>

        {/* Features */}
        <div>
          <h4 style={{ margin: '0 0 8px 0', color: '#666', fontSize: '0.875rem', textTransform: 'uppercase' }}>
            Features
          </h4>
          <div style={{ color: '#333' }}>
            <div><strong>Integrations:</strong> {numberOfActiveIntegrations}/{planData.TotalIntegrations}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <strong>Market Insights:</strong>
              <span style={{
                color: planData.IncludeMarketInsights ? '#4caf50' : '#f44336',
                fontWeight: 'bold'
              }}>
                {planData.IncludeMarketInsights ? '✓ Included' : '✗ Not Included'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Plan Modal */}
      {showCancelModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: 8,
            padding: 32,
            maxWidth: 500,
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#333', fontSize: '1.25rem' }}>
              Cancel Plan Confirmation
            </h3>
            <p style={{ margin: '0 0 16px 0', color: '#666', lineHeight: 1.5 }}>
              Are you sure you want to cancel your plan? Your plan will end on<br />{' '}
              {loadingCancellationDate ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <div style={{
                    width: 12,
                    height: 12,
                    border: '2px solid #f3f3f3',
                    borderTop: '2px solid #007bff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Loading...
                </span>
              ) : (
                <strong>{formatDate(cancellationDate)}</strong>
              )}.
            </p>
            <p style={{ margin: '0 0 24px 0', color: '#666', lineHeight: 1.5 }}>
              Your data will remain available until your current plan expires, and you can re-enable your plan at any time.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCancelModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#e0e0e0';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
              >
                Keep Plan
              </button>
              <button
                onClick={async () => {
                  try {
                    setIsCancellingPlan(true);
                    await cancelSubscription(false, cancellationDate); // Cancel at period end
                    setShowCancelModal(false);
                    showSuccess('Your plan will be canceled at the end of the current period. You\'ll continue to have access to all features until then.');
                    // Notify parent component to refresh data after a delay
                    if (onPlanCanceled) {
                      onPlanCanceled();
                    }
                  } catch (error: any) {
                    console.error('Error canceling plan:', error);
                    showError(`Failed to cancel plan: ${error.message}`);
                  } finally {
                    setIsCancellingPlan(false);
                  }
                }}
                disabled={isCancellingPlan}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  cursor: isCancellingPlan ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s ease',
                  opacity: isCancellingPlan ? 0.6 : 1
                }}
                onMouseOver={(e) => {
                  if (!isCancellingPlan) {
                    e.currentTarget.style.backgroundColor = '#d32f2f';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isCancellingPlan) {
                    e.currentTarget.style.backgroundColor = '#f44336';
                  }
                }}
              >
                {isCancellingPlan ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Processing...
                  </div>
                ) : (
                  'Cancel Plan'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivePlanDetails;
